import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Star, ThumbsUp, Meh, ThumbsDown, Tv, Film, Lock, Headphones, Mic, MoreVertical, RefreshCw, Trash2, Check, Loader2, Trophy, Music, Laugh, Search, Newspaper, Drama, Ghost, Heart, Clock, Users, Baby, Sparkles, Rocket, Globe, BookOpen, Dumbbell, Brain, Briefcase, Gamepad2, Plane, Utensils, Palette, Plus, Eye, Info, Calendar, Tag, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { AppHeader } from '../components/AppHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Podcast genre icon and color mapping
const genreConfig = {
  'Arts': { icon: Palette, color: 'bg-purple-50 text-purple-600 border-purple-200' },
  'Business': { icon: Briefcase, color: 'bg-blue-50 text-blue-600 border-blue-200' },
  'Comedy': { icon: Laugh, color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
  'Education': { icon: BookOpen, color: 'bg-green-50 text-green-600 border-green-200' },
  'Fiction': { icon: Drama, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  'Government': { icon: Globe, color: 'bg-slate-50 text-slate-600 border-slate-200' },
  'History': { icon: Clock, color: 'bg-orange-50 text-orange-600 border-orange-200' },
  'Health & Fitness': { icon: Dumbbell, color: 'bg-red-50 text-red-600 border-red-200' },
  'Kids & Family': { icon: Baby, color: 'bg-pink-50 text-pink-600 border-pink-200' },
  'Leisure': { icon: Gamepad2, color: 'bg-cyan-50 text-cyan-600 border-cyan-200' },
  'Music': { icon: Music, color: 'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-200' },
  'News': { icon: Newspaper, color: 'bg-gray-50 text-gray-600 border-gray-200' },
  'Religion & Spirituality': { icon: Sparkles, color: 'bg-violet-50 text-violet-600 border-violet-200' },
  'Science': { icon: Rocket, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  'Society & Culture': { icon: Users, color: 'bg-amber-50 text-amber-600 border-amber-200' },
  'Sports': { icon: Dumbbell, color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  'Technology': { icon: Brain, color: 'bg-sky-50 text-sky-600 border-sky-200' },
  'True Crime': { icon: Search, color: 'bg-neutral-50 text-neutral-700 border-neutral-300' },
  'TV & Film': { icon: Film, color: 'bg-rose-50 text-rose-600 border-rose-200' },
  'Travel': { icon: Plane, color: 'bg-teal-50 text-teal-600 border-teal-200' },
  'Food': { icon: Utensils, color: 'bg-orange-50 text-orange-600 border-orange-200' },
};

const getGenreConfig = (genre) => {
  return genreConfig[genre] || { icon: Mic, color: 'bg-slate-50 text-slate-600 border-slate-200' };
};

// Stat Card Component (matching Films page)
const StatCard = ({ label, value, icon, color }) => {
  const colorClasses = {
    coral: 'bg-[#F87171]/10 text-[#F87171]',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    slate: 'bg-slate-100 text-slate-600',
  };
  
  return (
    <Card className="border-0 shadow-lg bg-white/95 backdrop-blur-sm">
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-[#3D4F6F]">{value}</p>
          <p className="text-sm text-[#64748B]">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default function PodcastsListenedPage({ user }) {
  const navigate = useNavigate();
  const [listened, setListened] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Genre filter state
  const [selectedGenre, setSelectedGenre] = useState(null);
  
  // Re-rank dialog state
  const [rerankingPodcast, setRerankingPodcast] = useState(null);
  const [comparisonStep, setComparisonStep] = useState(false);
  const [existingItems, setExistingItems] = useState([]);
  const [comparisonItem, setComparisonItem] = useState(null);
  const [searchLow, setSearchLow] = useState(0);
  const [searchHigh, setSearchHigh] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // View details state
  const [viewingDetails, setViewingDetails] = useState(null);
  
  // Custom genre dialog state
  const [customGenreDialog, setCustomGenreDialog] = useState({ open: false, podcastId: null });
  const [customGenreInput, setCustomGenreInput] = useState('');
  
  const showScores = stats?.show_scores || false;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [listenedRes, statsRes] = await Promise.all([
        axios.get(`${API}/listened-podcasts`, { withCredentials: true }),
        axios.get(`${API}/listened-podcasts/stats`, { withCredentials: true })
      ]);
      setListened(listenedRes.data.podcasts || []);
      setStats(statsRes.data);
      
      // Auto-backfill genres if any items are missing them
      if (statsRes.data.missing_genres_count > 0) {
        try {
          await axios.post(`${API}/listened-podcasts/backfill-genres`, {}, { withCredentials: true });
          // Refetch stats to update genre breakdown
          const refreshedStats = await axios.get(`${API}/listened-podcasts/stats`, { withCredentials: true });
          setStats(refreshedStats.data);
        } catch {
          // Genre backfill failed - items will display without genre icons
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Logout failed but we should still clear local state
    }
    localStorage.removeItem('auth_token');
    navigate('/');
  };

  const handleRemove = async (item) => {
    if (!window.confirm(`Remove "${item.title}" from your listened list?`)) return;
    
    try {
      await axios.delete(`${API}/listened-podcasts/${item.id}`, { withCredentials: true });
      toast.success('Removed from listened');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const handleStartRerank = async (item) => {
    setRerankingPodcast(item);
    
    // Get other items in the same grade
    const sameGrade = listened.filter(p => p.grade === item.grade && p.id !== item.id)
      .sort((a, b) => a.rank_in_group - b.rank_in_group);
    setExistingItems(sameGrade);
    
    if (sameGrade.length === 0) {
      toast.info('No other podcasts in this grade to compare against');
      setRerankingPodcast(null);
    } else {
      setSearchLow(0);
      setSearchHigh(sameGrade.length);
      const midIndex = Math.floor(sameGrade.length / 2);
      setComparisonItem(sameGrade[midIndex]);
      setComparisonStep(true);
    }
  };

  const handleComparisonChoice = async (preferReranking) => {
    let newLow = searchLow;
    let newHigh = searchHigh;
    
    const currentIndex = existingItems.findIndex(i => i.id === comparisonItem.id);
    
    if (preferReranking) {
      newHigh = currentIndex;
    } else {
      newLow = currentIndex + 1;
    }
    
    setSearchLow(newLow);
    setSearchHigh(newHigh);
    
    if (newLow >= newHigh) {
      // Found position, save
      await saveRerank(newLow + 1);
    } else {
      const midIndex = Math.floor((newLow + newHigh) / 2);
      setComparisonItem(existingItems[midIndex]);
    }
  };

  const saveRerank = async (newRank) => {
    setSaving(true);
    try {
      const grade = rerankingPodcast.grade;
      const allInGrade = listened.filter(p => p.grade === grade).sort((a, b) => a.rank_in_group - b.rank_in_group);
      
      // Remove the item being re-ranked
      const withoutItem = allInGrade.filter(p => p.id !== rerankingPodcast.id);
      
      // Build bulk update
      const updates = [];
      let rank = 1;
      
      for (let i = 0; i <= withoutItem.length; i++) {
        if (i === newRank - 1) {
          updates.push({ id: rerankingPodcast.id, rank_in_group: rank });
          rank++;
        }
        if (i < withoutItem.length) {
          updates.push({ id: withoutItem[i].id, rank_in_group: rank });
          rank++;
        }
      }
      
      await axios.put(`${API}/listened-podcasts/bulk-rank`, { updates }, { withCredentials: true });
      
      toast.success(`Re-ranked to #${newRank}!`);
      resetRerankDialog();
      fetchData();
    } catch (error) {
      toast.error('Failed to update ranking');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePrimaryGenre = async (podcastId, genre) => {
    try {
      await axios.put(`${API}/listened-podcasts/${podcastId}/primary-genre`, {
        primary_genre: genre
      }, { withCredentials: true });
      
      // Update local state
      setListened(prev => prev.map(p => 
        p.id === podcastId ? { ...p, primary_genre: genre } : p
      ));
      
      // Refresh stats to update genre counts
      const statsRes = await axios.get(`${API}/listened-podcasts/stats`, { withCredentials: true });
      setStats(statsRes.data);
      
      toast.success('Genre updated');
    } catch (error) {
      toast.error('Failed to update genre');
    }
  };
  
  const openCustomGenreDialog = (podcastId) => {
    setCustomGenreDialog({ open: true, podcastId });
    setCustomGenreInput('');
  };
  
  const saveCustomGenre = async () => {
    if (!customGenreInput.trim() || !customGenreDialog.podcastId) return;
    await handleUpdatePrimaryGenre(customGenreDialog.podcastId, customGenreInput.trim());
    setCustomGenreDialog({ open: false, podcastId: null });
    setCustomGenreInput('');
  };

  const resetRerankDialog = () => {
    setRerankingPodcast(null);
    setComparisonStep(false);
    setExistingItems([]);
    setComparisonItem(null);
    setSearchLow(0);
    setSearchHigh(0);
  };

  // Sort listened by grade then rank
  const sortedListened = [...listened]
    .filter(item => !selectedGenre || (item.primary_genre || item.genres?.[0]) === selectedGenre)
    .sort((a, b) => {
      const gradeOrder = { liked: 0, alright: 1, disliked: 2 };
      const gradeCompare = gradeOrder[a.grade] - gradeOrder[b.grade];
      if (gradeCompare !== 0) return gradeCompare;
      return a.rank_in_group - b.rank_in_group;
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3D4F6F] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F87171] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#3D4F6F]">
      {/* Header with full navigation */}
      <AppHeader user={user} showBack={true} backPath="/podcasts" />

      <main className="container mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/90 rounded-xl flex items-center justify-center shadow-lg">
              <Headphones className="w-6 h-6 text-[#F87171]" />
            </div>
            <div>
              <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">Podcasts Listened</h1>
              <p className="text-white/80">{stats?.total_listened || 0} podcasts listened and ranked</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/discover/podcasts')}
            className="bg-white text-[#F87171] hover:bg-white/90 rounded-full"
            data-testid="add-listened-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Podcast
          </Button>
        </div>
        
        {/* Stats Cards (matching Films page layout) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Listened" value={stats?.total_listened || 0} icon={<Mic className="w-5 h-5" />} color="coral" />
          <StatCard label="Liked" value={stats?.by_grade?.liked || 0} icon={<ThumbsUp className="w-5 h-5" />} color="green" />
          <StatCard label="Alright" value={stats?.by_grade?.alright || 0} icon={<Meh className="w-5 h-5" />} color="amber" />
          <StatCard label="Didn't Like" value={stats?.by_grade?.disliked || 0} icon={<ThumbsDown className="w-5 h-5" />} color="slate" />
        </div>
        
        {/* Genre Breakdown - Clickable Filters */}
        {stats?.by_genre && Object.keys(stats.by_genre).length > 0 && (
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm mb-6">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-lg text-[#3D4F6F]">By Genre</CardTitle>
                {selectedGenre && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedGenre(null)}
                    className="text-xs text-[#64748B] hover:text-[#3D4F6F]"
                  >
                    Clear filter
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {Object.entries(stats.by_genre).map(([genre, count]) => {
                  const config = getGenreConfig(genre);
                  const IconComponent = config.icon;
                  const isSelected = selectedGenre === genre;
                  return (
                    <button 
                      key={genre} 
                      onClick={() => setSelectedGenre(isSelected ? null : genre)}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all hover:scale-105 text-left ${
                        isSelected 
                          ? 'ring-2 ring-[#F87171] ring-offset-2 ' + config.color
                          : config.color
                      }`}
                    >
                      <div className="flex-shrink-0">
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{genre}</p>
                        <p className="text-xs opacity-75">{count} {count === 1 ? 'podcast' : 'podcasts'}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Score Unlock Progress */}
        {!showScores && (
          <Card className="mb-6 bg-white/95 backdrop-blur-sm border-0 shadow-xl" data-testid="score-unlock-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#F87171]/10 rounded-full flex items-center justify-center">
                  <Lock className="w-7 h-7 text-[#F87171]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-outfit font-semibold text-[#3D4F6F] text-lg" data-testid="score-unlock-title">Unlock Your Podcast Scores</h3>
                  <p className="text-[#64748B] text-sm" data-testid="score-unlock-message">
                    Listen to {10 - (stats?.total_listened || 0)} more podcasts to reveal your personalized scores
                  </p>
                  <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#F87171] rounded-full transition-all"
                      style={{ width: `${Math.min(((stats?.total_listened || 0) / 10) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Listened Podcasts List (matching Films page) */}
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-lg text-[#3D4F6F]">
              {selectedGenre 
                ? `${selectedGenre} (${sortedListened.length} podcasts)`
                : `Your Rankings (${listened.length} podcasts)`
              }
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sortedListened.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Mic className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium mb-2">No podcasts listened yet</p>
                <p className="text-sm mb-4">Start discovering and ranking podcasts!</p>
                <Button className="bg-[#F87171] hover:bg-[#EF4444] text-white" onClick={() => navigate('/discover/podcasts')}>
                  Discover Podcasts
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {sortedListened.map((item, index) => {
                  const gradeConfig = {
                    liked: { icon: ThumbsUp, color: 'bg-green-100 text-green-700' },
                    alright: { icon: Meh, color: 'bg-amber-100 text-amber-700' },
                    disliked: { icon: ThumbsDown, color: 'bg-slate-100 text-slate-600' }
                  };
                  const grade = gradeConfig[item.grade] || gradeConfig.alright;
                  const GradeIcon = grade.icon;
                  const unifiedRank = index + 1;
                  
                  // Get primary genre (use primary_genre if set, otherwise first in array)
                  const primaryGenre = item.primary_genre || item.genres?.[0];
                  const genreInfo = primaryGenre ? getGenreConfig(primaryGenre) : null;
                  const GenreIcon = genreInfo?.icon || Mic;
                  const hasMultipleGenres = item.genres?.length > 1;
                  
                  return (
                    <div
                      key={item.id}
                      className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors group"
                      data-testid={`listened-podcast-${item.id}`}
                    >
                      {/* Rank */}
                      <span className="font-outfit font-bold text-lg sm:text-xl text-[#3D4F6F] w-6 sm:w-8 text-center flex-shrink-0 pt-1">
                        {unifiedRank}
                      </span>
                      
                      {/* Podcast Image - Clickable */}
                      <div 
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 cursor-pointer hover:scale-105 transition-transform"
                        onClick={() => setViewingDetails(item)}
                      >
                        {(item.image || item.thumbnail) ? (
                          <img src={item.image || item.thumbnail} alt={item.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Mic className="w-5 h-5 text-slate-300" />
                          </div>
                        )}
                      </div>
                      
                      {/* Content - Two Line Layout */}
                      <div className="flex-1 min-w-0">
                        {/* Line 1: Title + Score */}
                        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setViewingDetails(item)}>
                          <h3 className="font-medium text-sm sm:text-base text-[#3D4F6F] truncate flex-1">{item.title}</h3>
                          {/* Score badge */}
                          {showScores && item.calculated_score !== null && item.calculated_score !== undefined && (
                            <div className="flex items-center gap-0.5 bg-amber-50 px-1.5 py-0.5 rounded-full flex-shrink-0" data-testid="score-display">
                              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                              <span className="font-medium text-xs text-amber-700">{Number(item.calculated_score).toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Line 2: Date on left, Icons on right */}
                        <div className="flex items-center justify-between mt-1">
                          {/* Left: Publisher/date */}
                          <div className="flex items-center gap-1 text-xs text-[#94A3B8]">
                            {item.publisher && (
                              <span className="truncate max-w-[100px]">{item.publisher}</span>
                            )}
                            {item.listen_date && (
                              <span className="hidden sm:flex items-center gap-1">
                                <span className="mx-1">•</span>
                                <Calendar className="w-3 h-3" />
                                {(() => {
                                  try {
                                    return new Date(item.listen_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                                  } catch {
                                    return '';
                                  }
                                })()}
                              </span>
                            )}
                          </div>
                          
                          {/* Right: Icons */}
                          <div className="flex items-center gap-1">
                            {primaryGenre && (
                              <div className={`p-1 rounded ${genreInfo?.color || 'bg-slate-100 text-slate-600'}`} title={primaryGenre}>
                                <GenreIcon className="w-3 h-3" />
                              </div>
                            )}
                            <div className={`p-1 rounded-full ${grade.color}`}>
                              <GradeIcon className="w-3 h-3" />
                            </div>
                          </div>
                        </div>
                        
                        {/* Comment preview on desktop */}
                        {item.comment && (
                          <p className="hidden sm:block text-xs text-[#94A3B8] truncate mt-0.5 italic">&ldquo;{item.comment}&rdquo;</p>
                        )}
                      </div>
                      
                      {/* Actions Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 p-0 text-[#64748B] hover:text-[#3D4F6F] flex-shrink-0">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setViewingDetails(item)} className="sm:hidden">
                            <Info className="w-4 h-4 mr-2 text-blue-600" />View Details
                          </DropdownMenuItem>
                          {/* Change Genre option - always show since we have custom option */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <div className="flex items-center px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-100 rounded-sm">
                                <Tag className="w-4 h-4 mr-2 text-[#F87171]" />
                                Change Genre
                                <ChevronRight className="w-4 h-4 ml-auto" />
                              </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="left" align="start" className="min-w-[140px]">
                              {(item.genres || []).map((genre) => {
                                const gInfo = getGenreConfig(genre);
                                const GIcon = gInfo?.icon || Mic;
                                const isSelected = genre === primaryGenre;
                                return (
                                  <DropdownMenuItem 
                                    key={genre}
                                    onClick={() => handleUpdatePrimaryGenre(item.id, genre)}
                                    className={`cursor-pointer ${isSelected ? 'bg-[#F87171]/10' : ''}`}
                                  >
                                    <GIcon className={`w-4 h-4 mr-2 ${gInfo?.color?.split(' ')[1] || 'text-slate-600'}`} />
                                    {genre}
                                    {isSelected && <Check className="w-4 h-4 ml-auto text-[#F87171]" />}
                                  </DropdownMenuItem>
                                );
                              })}
                              {/* Custom Genre Option */}
                              <DropdownMenuItem 
                                onClick={() => openCustomGenreDialog(item.id)}
                                className="cursor-pointer text-purple-600"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Custom Genre...
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <DropdownMenuItem onClick={() => handleStartRerank(item)}>
                            <RefreshCw className="w-4 h-4 mr-2" />Re-rank
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleRemove(item)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Re-rank Dialog */}
      <Dialog open={!!rerankingPodcast && comparisonStep} onOpenChange={() => resetRerankDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3D4F6F] flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />Which do you prefer?
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Re-ranking &ldquo;{rerankingPodcast?.title}&rdquo; against your {rerankingPodcast?.grade} podcasts
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <div className="grid grid-cols-2 gap-4">
              {/* Reranking Item */}
              <button
                onClick={() => handleComparisonChoice(true)}
                disabled={saving}
                className="p-4 rounded-xl border-2 border-slate-200 hover:border-[#F87171] hover:bg-[#F87171]/5 transition-all text-left disabled:opacity-50"
              >
                <div className="aspect-square rounded-lg overflow-hidden bg-slate-100 mb-3">
                  {rerankingPodcast?.image ? (
                    <img src={rerankingPodcast.image} alt={rerankingPodcast.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Mic className="w-10 h-10 text-slate-300" /></div>
                  )}
                </div>
                <p className="font-medium text-[#3D4F6F] line-clamp-2 text-sm">{rerankingPodcast?.title}</p>
                <p className="text-xs text-[#64748B] mt-1">Re-ranking</p>
              </button>
              
              {/* Comparison Item */}
              <button
                onClick={() => handleComparisonChoice(false)}
                disabled={saving}
                className="p-4 rounded-xl border-2 border-slate-200 hover:border-[#F87171] hover:bg-[#F87171]/5 transition-all text-left disabled:opacity-50"
              >
                <div className="aspect-square rounded-lg overflow-hidden bg-slate-100 mb-3">
                  {comparisonItem?.image ? (
                    <img src={comparisonItem.image} alt={comparisonItem.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Mic className="w-10 h-10 text-slate-300" /></div>
                  )}
                </div>
                <p className="font-medium text-[#3D4F6F] line-clamp-2 text-sm">{comparisonItem?.title}</p>
                <p className="text-xs text-[#64748B] mt-1">#{comparisonItem?.rank_in_group}</p>
              </button>
            </div>
            
            {saving && (
              <div className="flex items-center justify-center mt-4">
                <Loader2 className="w-6 h-6 animate-spin text-[#F87171]" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Details Dialog - Mobile view */}
      <Dialog open={!!viewingDetails} onOpenChange={() => setViewingDetails(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3D4F6F]">{viewingDetails?.title}</DialogTitle>
          </DialogHeader>
          {viewingDetails && (
            <div className="space-y-4">
              <div className="flex gap-4">
                {viewingDetails.image && (
                  <img 
                    src={viewingDetails.image} 
                    alt={viewingDetails.title}
                    className="w-20 h-20 object-cover rounded-lg shadow"
                  />
                )}
                <div className="flex-1 space-y-2">
                  <p className="text-sm text-[#64748B]">{viewingDetails.publisher}</p>
                  {viewingDetails.listen_date && (
                    <p className="text-sm text-[#64748B] flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(viewingDetails.listen_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                  {viewingDetails.is_first_listen && (
                    <p className="text-sm text-purple-600 flex items-center gap-1">
                      <Headphones className="w-3.5 h-3.5" />
                      First time listen
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#64748B]">Rating:</span>
                    {viewingDetails.grade === 'liked' && <span className="text-green-600 flex items-center gap-1"><ThumbsUp className="w-4 h-4"/> Liked</span>}
                    {viewingDetails.grade === 'alright' && <span className="text-amber-600 flex items-center gap-1"><Meh className="w-4 h-4"/> Alright</span>}
                    {viewingDetails.grade === 'disliked' && <span className="text-slate-600 flex items-center gap-1"><ThumbsDown className="w-4 h-4"/> Disliked</span>}
                  </div>
                </div>
              </div>
              {viewingDetails.comment && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-[#64748B] mb-1">Your thoughts:</p>
                  <p className="text-sm text-[#3D4F6F] italic">&ldquo;{viewingDetails.comment}&rdquo;</p>
                </div>
              )}
              {viewingDetails.genres?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {viewingDetails.genres.map((g, i) => (
                    <span key={typeof g === 'object' ? (g.id || g.name || i) : `${g}-${i}`} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs">
                      {typeof g === 'object' ? g.name : g}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Custom Genre Dialog */}
      <Dialog open={customGenreDialog.open} onOpenChange={(open) => !open && setCustomGenreDialog({ open: false, podcastId: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#3D4F6F]">Enter Custom Genre</DialogTitle>
            <DialogDescription>
              Enter your own genre for this podcast
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="e.g., Tech News, Interview..."
            value={customGenreInput}
            onChange={(e) => setCustomGenreInput(e.target.value)}
            className="mt-2"
            data-testid="custom-genre-input"
            onKeyDown={(e) => e.key === 'Enter' && saveCustomGenre()}
          />
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCustomGenreDialog({ open: false, podcastId: null })}>
              Cancel
            </Button>
            <Button 
              onClick={saveCustomGenre}
              disabled={!customGenreInput.trim()}
              className="bg-purple-500 hover:bg-purple-600"
            >
              Save Genre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
